/**
 * Search Events Logger (SE-4010)
 *
 * Handles logging of player discovery events (search and filters)
 * to PostHog Analytics.
 * @module features/analytics/utils/searchEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  ISearchAnalyticsService,
  TFilterAppliedParams,
  TSearchPerformedParams,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Search events analytics logger.
 *
 * Provides methods for logging player search and filter usage.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * AnalyticsTracker.search.logSearchPerformed({ query_length: 7 });
 * ```
 */
export const searchEventLogger: ISearchAnalyticsService = {
  /**
   * Logs a search performed event.
   * @param params - Search performed parameters
   */
  logSearchPerformed: async (params: TSearchPerformedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.SEARCH.PERFORMED, params);
  },

  /**
   * Logs a filter applied event.
   * @param params - Filter applied parameters
   */
  logFilterApplied: async (params: TFilterAppliedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.SEARCH.FILTER_APPLIED, params);
  },
};
